import { type ReactNode, createElement, useEffect } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { useRouter } from "@tanstack/react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import _ from "lodash";
import { CrudEntry } from "./mma/types.gen";
import { buildGroups } from "@/components/sidebar/sidebar";

export const isClient = typeof window !== "undefined";

export type QueryErrorWithStatus = Error & { status?: number };

export type ContextUser = {
  username: string;
  displayName: string;
  profile: string;
  token: string;
  baseUrl: string;
  clientIp: string;
  passwordExpiredFlow?: boolean;
  accessControl: Record<string, CrudEntry>;
};

export const createErrorWithStatus = (message: string, status: number): QueryErrorWithStatus => {
  const error = new Error(message) as QueryErrorWithStatus;
  error.status = status;
  return error;
};

export const isForbiddenError = (error: unknown): boolean => {
  return (
    typeof error === "object" && error !== null && "status" in error && (error as QueryErrorWithStatus).status === 403
  );
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const useScrollHashUpdater = (sectionNames: string[]) => {
  const router = useRouter();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isClient || isMobile === true) return;

    const HEADER_OFFSET = 325;

    let hashUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastActiveIndex: number | null = null;

    const handleScroll = () => {
      if (!mainContentContainer) return;

      let activeIndex = -1;
      sections.forEach((section, i) => {
        if (!section) return;
        const top = section.getBoundingClientRect().top - HEADER_OFFSET;
        if (top <= 0) {
          activeIndex = i;
        }
      });

      if (activeIndex === -1) {
        activeIndex = sections.findIndex((section) => section !== null);
      }

      const atBottom =
        mainContentContainer.scrollTop + mainContentContainer.clientHeight >= mainContentContainer.scrollHeight - 1;
      if (atBottom) {
        for (let i = sections.length - 1; i >= 0; i--) {
          if (sections[i]) {
            activeIndex = i;
            break;
          }
        }
      }

      if (activeIndex < 0 || activeIndex === lastActiveIndex) return;
      lastActiveIndex = activeIndex;

      const activeSection = sections[activeIndex];
      if (!activeSection) return;

      const id = activeSection.id;

      clearTimeout(hashUpdateTimeout || undefined);

      hashUpdateTimeout = setTimeout(() => {
        if (router.state.location.hash !== id) {
          router.navigate({ hash: id, replace: true, hashScrollIntoView: false, resetScroll: false });
        }
      }, 150);
    };

    const sectionIds = sectionNames.map((name) => name?.toLowerCase().replace(/ /g, "-"));
    const sections = sectionIds.map((id) => document.getElementById(id));

    const mainContentContainer = document.getElementById("main-content-container");
    if (mainContentContainer) {
      mainContentContainer.addEventListener("scroll", handleScroll);
      handleScroll();
    }

    // Cleanup function
    return () => {
      if (mainContentContainer) {
        mainContentContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [router, sectionNames, isMobile]);
};

// Zero-dependency allowlist HTML sanitizer — works in both browser and SSR (no Node.js built-ins).
// Only allows basic formatting tags the app actually uses in server-provided setting descriptions.
const ALLOWED_TAGS = new Set([
  "b",
  "i",
  "em",
  "strong",
  "br",
  "p",
  "ul",
  "ol",
  "li",
  "div",
  "span",
  "a",
  "dl",
  "dt",
  "dd",
  "mark",
  "code",
]);
// Subset of tags that may carry safe attributes
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  span: new Set(["class"]),
  mark: new Set(["class"]),
  a: new Set(["href", "target", "rel"]),
};

export const sanitizeHtml = (dirtyHtml: unknown): string => {
  if (typeof dirtyHtml !== "string") return "";
  // Remove dangerous block-level tags and everything they contain
  const stripped = dirtyHtml.replace(
    /<(script|style|iframe|object|embed|form|input|button|textarea|select|base|link|meta)\b[^>]*>[\s\S]*?<\/\1>/gi,
    ""
  );

  return stripped.replace(/<(\/?[a-z][a-z0-9]*)\b([^>]*)>/gi, (match, rawTag: string, attrs: string) => {
    const tag = rawTag.replace(/^\//, "").toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) return "";

    // Closing tag
    if (rawTag.startsWith("/")) return `</${tag}>`;

    // Self-closing (e.g. <br/>)
    const selfClose = match.trimEnd().endsWith("/>") ? " /" : "";

    const allowedAttrNames = ALLOWED_ATTRS[tag];
    if (!allowedAttrNames) return `<${tag}${selfClose}>`;

    // Retain only explicitly allowed attributes, stripping values that look like JS
    const safeAttrs = [...attrs.matchAll(/([a-z][a-z0-9-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi)]
      .filter(([, name]) => allowedAttrNames.has(name.toLowerCase()))
      .filter(([, , dq, sq]) => !/javascript\s*:/i.test(dq ?? sq ?? ""))
      .map(([, name, dq, sq]) => `${name.toLowerCase()}="${(dq ?? sq ?? "").replace(/"/g, "&quot;")}"`);

    return safeAttrs.length ? `<${tag} ${safeAttrs.join(" ")}${selfClose}>` : `<${tag}${selfClose}>`;
  });
};

export const highlightText = (text: string, searchTerm: string): ReactNode => {
  if (!searchTerm) return text;

  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);

  if (parts.length === 1) return text;

  return parts.map((part, i) =>
    i % 2 === 1 ? createElement("mark", { key: i, className: "bg-yellow-200 dark:bg-yellow-800 rounded" }, part) : part
  );
};

export const calculateResetValues = (organisationSettings: any, organisationSettingTypes: any) => {
  let affectedItems: string[] = [];
  if (_.isEmpty(organisationSettings)) return [];
  const organisationSettingsKeys = Object.keys(organisationSettings);
  organisationSettingTypes.forEach((settingGroup: any) => {
    settingGroup.settings?.forEach((setting: any) => {
      if (organisationSettingsKeys.includes(setting.name)) {
        affectedItems.push(setting.displayName);
      }
    });
  });
  return affectedItems;
};

export const checkIfUserMayRead = (
  userProfileId: string | null | undefined,
  accessControl: Record<string, CrudEntry> | undefined,
  resource: string
) => {
  return !!(userProfileId === null || userProfileId === "0" || accessControl?.[resource]?.includes("R"));
};

export const hasPermission = (user: ContextUser | null | undefined, permission: string): boolean => {
  if (!user) return false;
  return checkIfUserMayRead(user.profile, user.accessControl, permission);
};

export const checkIfUserMayWrite = (
  userProfileId: string | null | undefined,
  accessControl: Record<string, CrudEntry> | undefined,
  resource: string
) => {
  return !!(userProfileId === null || userProfileId === "0" || accessControl?.[resource]?.includes("C"));
};

export const hasWritePermission = (user: ContextUser | null | undefined, resource: string): boolean => {
  if (!user) return false;
  return checkIfUserMayWrite(user.profile, user.accessControl, resource);
};

export const checkIfUserMayUpdate = (
  userProfileId: string | null | undefined,
  accessControl: Record<string, CrudEntry> | undefined,
  resource: string
) => {
  return !!(userProfileId === null || userProfileId === "0" || accessControl?.[resource]?.includes("U"));
};

export const hasUpdatePermission = (user: ContextUser | null | undefined, resource: string): boolean => {
  if (!user) return false;
  return checkIfUserMayUpdate(user.profile, user.accessControl, resource);
};

export const checkIfUserMayDelete = (
  userProfileId: string | null | undefined,
  accessControl: Record<string, CrudEntry> | undefined,
  resource: string
) => {
  return !!(userProfileId === null || userProfileId === "0" || accessControl?.[resource]?.includes("D"));
};

export const hasDeletePermission = (user: ContextUser | null | undefined, resource: string): boolean => {
  if (!user) return false;
  return checkIfUserMayDelete(user.profile, user.accessControl, resource);
};

export const getPermissionRestraintMessage = (action: string): string => {
  return `You do not have permission to ${action}. This action is restricted due to insufficient access privileges.`;
};

export const getNextRedirectRoute = (user: ContextUser): string => {
  const groups = buildGroups(user, hasPermission);

  for (const group of groups) {
    for (const item of group.items) {
      if (item.permission !== true) continue;

      if ("url" in item && item.url) {
        return item.url;
      }

      if ("items" in item && item.items) {
        const permittedSubItem = item.items.find((sub) => "permission" in sub && sub.permission === true && sub.url);
        if (permittedSubItem?.url) {
          return permittedSubItem.url;
        }

        const firstSubItem = item.items.find((sub) => sub.url);
        if (firstSubItem?.url) {
          return firstSubItem.url;
        }
      }
    }
  }

  return "/401";
};

export const transformToNull = (object: any) => {
  Object.entries(object).forEach(([key, value]) => {
    if (value === "") {
      object[key] = null;
    }
  });
  return object;
};

export const exportToCsv = (data: any[], columns: string[]) => {
  const orderedColumns = data[0] != null ? Object.keys(data[0]).filter((key) => columns.includes(key)) : columns;

  const headers = orderedColumns
    .map((column) => {
      return `"${column.replace(/"/g, '""')}"`;
    })
    .join(",");
  const rows = data
    .map((row) => {
      return orderedColumns
        .map((key) => {
          const value = row[key];
          if (typeof value === "string") {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(",");
    })
    .join("\n");
  return `${headers}\n${rows}`;
};

export const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
};

export const formatResourceLabel = (resource: string): string => {
  return resource.charAt(0) + resource.slice(1).toLowerCase().replace(/_/g, " ");
};
